import { useId } from 'react';
import { cn } from '../../lib/utils';

/**
 * Disclosure row built on a real <button> with aria-expanded/aria-controls, so
 * screen readers and keyboard users get correct semantics.
 *
 * The open/close transition uses grid-template-rows rather than an animated
 * height, which avoids measuring the content and stays smooth on mobile.
 */
export default function AccordionItem({ question, answer, isOpen, onToggle, className }) {
  const id = useId();
  const panelId = `${id}-panel`;
  const buttonId = `${id}-button`;

  return (
    <div className={cn('border-b border-line last:border-b-0', className)}>
      <h3>
        <button
          type="button"
          id={buttonId}
          aria-expanded={isOpen}
          aria-controls={panelId}
          onClick={onToggle}
          className="group flex w-full items-center justify-between gap-6 py-5 text-left"
        >
          <span
            className={cn(
              'text-base md:text-[1.0625rem] font-semibold transition-colors duration-200',
              isOpen ? 'text-brand' : 'text-white group-hover:text-brand-200',
            )}
          >
            {question}
          </span>
          <span
            className={cn(
              'grid h-8 w-8 shrink-0 place-items-center rounded-full border transition-all duration-300 ease-smooth',
              isOpen
                ? 'rotate-180 border-line-brand bg-brand/10 text-brand'
                : 'border-line text-content-muted group-hover:border-line-strong group-hover:text-white',
            )}
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M19 9l-7 7-7-7"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </button>
      </h3>

      <div
        id={panelId}
        role="region"
        aria-labelledby={buttonId}
        className={cn(
          'grid transition-[grid-template-rows] duration-300 ease-smooth',
          isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        )}
      >
        <div className="overflow-hidden">
          <p className="pb-6 pr-12 text-content-muted leading-relaxed text-pretty">{answer}</p>
        </div>
      </div>
    </div>
  );
}
